import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Input, InputProps } from 'semantic-ui-react'
// @ts-ignore - no types available for react-load-script
import Script from 'react-load-script'
import { get, isEmpty, trim, isEqual } from 'lodash'
import logger from '@adc/logger'
import classnames from 'classnames'
import { commonPropTypes, formFieldWrapper } from '../formFieldWrapper'
import { FormFieldCommonProps } from '../../../types'
import {
  ADDRESS_FIELD_LABEL,
  GOOGLE_ADDRESS_PARTS,
  MAP_GOOGLE_PROPS_TO_FORM_PROPS,
  STREET_NAME_NUMBER_REGEX,
} from './constants'
import {
  getAddressMapFromPlace,
  mapPropsFromSourceWithMap,
} from './utils/index'

/*
 * AddressSuggestionField has a dependency on google maps places api
 * This component will always return onChange a street_name and street_number as well as the selectedAddressText
 * The street_name and street_number are regexed if the user free formed the input
 * Otherwise those values will be populated from the google autocomplete selection
 * The value returned from this component are suggestions to fill in the other address fields
 *
 * Refer to for api responses and usage:
 * https://developers.google.com/maps/documentation/javascript/places-autocomplete
 * If passing in a value see componentDidMount
 */

interface GoogleApiConfig {
  channelName: string
  apiKey: string
}

export interface SelectedAddress {
  street_number?: string
  street_name?: string
  selectedAddressText?: string
  is_option_selected?: boolean
  neighborhood?: string
  postal_code?: string
  country_primary_subdivision?: string
  country_secondary_subdivision?: string
  coordinates?: {
    lat: number
    lon: number
  }
  street_unit?: string
  municipality?: string
  country_code?: string
  [key: string]: unknown
}

interface AddressValue extends SelectedAddress {
  selectedAddressText?: string
}

interface GoogleAddressParts {
  [key: string]: 'short_name' | 'long_name'
}

interface MapGooglePropsToFormProps {
  street_number: string
  route: string
  [key: string]: string
}

interface GoogleAutocompleteOptions {
  types?: string[]
  [key: string]: unknown
}

export interface AddressSuggestionFieldProps
  extends Omit<FormFieldCommonProps, 'onChange' | 'onBlur'> {
  compact?: boolean
  className?: string
  googleApiConfig: GoogleApiConfig
  googleAutocompleteOptions?: GoogleAutocompleteOptions
  googleAddressParts?: GoogleAddressParts
  mapGooglePropsToFormProps?: MapGooglePropsToFormProps
  displayFormattedAddress?: boolean
  value?: AddressValue
  onChange?: (value: SelectedAddress) => void
  onBlur?: (value: SelectedAddress) => void
  onFocus?: () => void
  label?: string
  loading?: boolean
  error?: boolean | string
  name?: string
  touched?: boolean
}

interface AddressState {
  selectedAddressText?: string
  selectedAddress: SelectedAddress
}

export const AddressSuggestionField: React.FC<AddressSuggestionFieldProps> = ({
  compact = false,
  className = '',
  elmId,
  placeholder,
  googleApiConfig,
  googleAutocompleteOptions,
  googleAddressParts = GOOGLE_ADDRESS_PARTS,
  mapGooglePropsToFormProps = MAP_GOOGLE_PROPS_TO_FORM_PROPS,
  displayFormattedAddress,
  value,
  onChange,
  onBlur,
  loading: _loading,
  onFocus: _onFocus,
  label: _label,
  ...rest
}) => {
  const [isMounted, setIsMounted] = useState(false)
  const [selectedAddressText, setSelectedAddressText] = useState('')
  const [selectedAddress, setSelectedAddress] = useState<SelectedAddress>({})

  const addressInputRef = useRef<HTMLInputElement | null>(null)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const autocompleteRef = useRef<any>(null)
  const prevValueRef = useRef<AddressValue | undefined>(value)

  /**
   * Returns an object with street_name and street_number filled in appropriated based on regex
   * @params inputText String the text in the input the user interacts with
   */
  const streetNameNumberRegex = useCallback(
    (inputText: string | undefined): SelectedAddress => {
      const streetNameKey = mapGooglePropsToFormProps.route
      const streetNumberKey = mapGooglePropsToFormProps.street_number
      const address: SelectedAddress = {}
      // clear the selectedAddress since user made an input change
      // attempt to parse the street_name and street_number
      const matches = STREET_NAME_NUMBER_REGEX.exec(inputText || '')
      if (matches) {
        address[streetNumberKey] = matches[1]
        address[streetNameKey] = matches[2]
      } else {
        address[streetNumberKey] = ''
        address[streetNameKey] = trim(inputText)
      }
      return address
    },
    [mapGooglePropsToFormProps]
  )

  const updateState = useCallback(
    (newValue: AddressValue | undefined): AddressState => {
      let nextState: AddressState = {
        selectedAddress: {},
        selectedAddressText: '',
      }

      if (!isEmpty(newValue)) {
        // ensure the street_number and street_name are filled in
        const addressText = newValue?.selectedAddressText || ''
        const address = {
          ...streetNameNumberRegex(addressText),
          ...newValue,
        }
        nextState = {
          selectedAddress: address,
          selectedAddressText: addressText,
        }
      }

      return nextState
    },
    [streetNameNumberRegex]
  )

  // componentDidMount effect
  useEffect(() => {
    setIsMounted(true)
    const initialState = updateState(value)
    setSelectedAddressText(initialState.selectedAddressText || '')
    setSelectedAddress(initialState.selectedAddress)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Handle value changes from props (replaces UNSAFE_componentWillReceiveProps)
  useEffect(() => {
    if (!isEqual(value, prevValueRef.current)) {
      const newState = updateState(value)
      setSelectedAddressText(newState.selectedAddressText || '')
      setSelectedAddress(newState.selectedAddress)
    }
    prevValueRef.current = value
  }, [value, updateState])

  // componentWillUnmount effect
  useEffect(() => {
    return () => {
      addressInputRef.current = null
    }
  }, [])

  const loadScriptError = useCallback(() => {
    logger.warn('Could not load Google Places Api.')
  }, [])

  /*
   * The user has selected an option from the Google Maps Api results
   * Save the necessary props to a state object and call parent callback
   */
  const addressOptionSelected = useCallback(() => {
    if (!autocompleteRef.current) return

    // Get the place details from the autocomplete object.
    const place = autocompleteRef.current.getPlace()
    const formattedAddress = place.formatted_address

    // get the address parts
    let address: SelectedAddress = getAddressMapFromPlace(
      place,
      googleAddressParts
    ) as unknown as SelectedAddress
    const neighborhood = get(address, 'neighborhood', null)
    // map selectedAddress
    address = mapPropsFromSourceWithMap(
      address,
      mapGooglePropsToFormProps
    ) as SelectedAddress
    // preserves the `neighborhood` property if available.
    // it could also be returned in the `municipality` property, but only if `locality` is not found first.
    if (neighborhood) {
      address.neighborhood = neighborhood
    }
    const builtSelectedAddressText = `${address.street_number || ''} ${
      address.street_name || ''
    }`
    // the string the user sees for the text input
    const addressText = displayFormattedAddress
      ? formattedAddress
      : builtSelectedAddressText
    // make sure we have street_name and street_number
    if (
      !address[mapGooglePropsToFormProps.route] ||
      !address[mapGooglePropsToFormProps.street_number]
    ) {
      address = {
        ...address,
        ...streetNameNumberRegex(builtSelectedAddressText),
      }
    }
    // add a flag to indicate this is an option selected from the Google Maps Api results
    address.is_option_selected = true

    setSelectedAddress(address)
    setSelectedAddressText(addressText || '')
    // fire onChange here since this is the Api value we want sent to the parent
    onChange?.({ selectedAddressText: addressText, ...address })
  }, [
    googleAddressParts,
    mapGooglePropsToFormProps,
    displayFormattedAddress,
    streetNameNumberRegex,
    onChange,
  ])

  /*
   * The Google Places `place_changed` listener is registered once (when the
   * Maps script loads) and would otherwise permanently capture the
   * `addressOptionSelected` closure from that render. Keep a ref pointing to
   * the latest callback so the listener always invokes the current closure
   * (with the parent's up-to-date `onChange`), preventing stale state
   * overwrites in consumers (CORE-336).
   */
  const addressOptionSelectedRef = useRef(addressOptionSelected)
  useEffect(() => {
    addressOptionSelectedRef.current = addressOptionSelected
  }, [addressOptionSelected])

  /*
   * google maps has succesfully loaded
   */
  const loadScriptSuccess = useCallback(() => {
    // Semantic Input has an inputRef
    const addressInput = addressInputRef.current
    if (!addressInput) return

    const options = googleAutocompleteOptions || {}
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const autocomplete = new (window as any).google.maps.places.Autocomplete(
      addressInput,
      {
        types: ['address'],
        ...options,
      }
    )

    // When the user selects an address from the dropdown, populate the address state.
    // Invoke through the ref so the once-registered listener always calls the
    // latest callback instead of a stale closure.
    autocomplete?.addListener('place_changed', () =>
      addressOptionSelectedRef.current()
    )
    autocompleteRef.current = autocomplete
  }, [googleAutocompleteOptions])

  /*
   * Managed Component needs to track the user input and update the value so autocomplete API can hook into
   */
  const handleChange = useCallback(
    (_: React.ChangeEvent<HTMLInputElement>, data: { value: string }) => {
      const addressText = data.value

      // clear the selectedAddress since user made an input change
      // attempt to parse the street_name and street_number
      const address = streetNameNumberRegex(addressText)
      setSelectedAddressText(addressText)
      setSelectedAddress(address)
      // input field is free form
      onChange?.({ selectedAddressText: addressText, ...address })
    },
    [streetNameNumberRegex, onChange]
  )

  const handleBlur = useCallback(() => {
    // input may not be defined depending on usage
    onBlur?.({ selectedAddressText, ...selectedAddress })
  }, [selectedAddressText, selectedAddress, onBlur])

  /*
   * Store internal ref to the actual input needed for the autocomplete Api
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const addressInput = useCallback((input: any) => {
    addressInputRef.current = input?.inputRef?.current ?? null
  }, [])

  const newClassName = classnames({
    compact,
    [className]: true,
  })

  const { channelName, apiKey } = googleApiConfig

  return (
    <div>
      <Input
        autoComplete='new-address'
        className={newClassName}
        ref={addressInput}
        placeholder={placeholder || ADDRESS_FIELD_LABEL}
        data-elm-id={`${elmId}_input`}
        onChange={handleChange}
        onBlur={handleBlur}
        value={selectedAddressText}
        {...(rest as InputProps)}
      />
      {isMounted && (
        <Script
          url={`https://maps.googleapis.com/maps/api/js?language=en&key=${apiKey}&libraries=places&channel=${channelName}`}
          onError={loadScriptError}
          onLoad={loadScriptSuccess}
        />
      )}
    </div>
  )
}

/*
 * Wrap the AddressSuggestionField component so that it plays well with redux-form
 */
const AddressSuggestionFieldWrapped = formFieldWrapper(
  (fieldProps: AddressSuggestionFieldProps, showError: boolean) => {
    const { elmId, placeholder, ...rest } = fieldProps

    return (
      <AddressSuggestionField
        placeholder={placeholder}
        error={showError}
        elmId={elmId}
        {...rest}
      />
    )
  }
) as unknown as React.FC<AddressSuggestionFieldProps> & {
  propTypes?: typeof commonPropTypes
}

AddressSuggestionFieldWrapped.propTypes = commonPropTypes

export default AddressSuggestionFieldWrapped
// A parent app can use these to transform data
export { mapPropsFromSourceWithMap, MAP_GOOGLE_PROPS_TO_FORM_PROPS }
